import { clearGlobalModal, setGlobalModal } from '@composables/useModal.js'
import { useStatus, useAsync, addToast } from '@composables'
import { STATUS_NOT_ACCEPTED } from '@config/options.js'
import { MODAL_LOGIN } from '@config/globalModals.js'
import { BUTTON_ANIMATION_DURATION } from '@config'
import { PORTAL_STUDIO } from '@config/auth.js'
import { computed, readonly, ref } from 'vue'
import { GAME_ID } from '@config/game.js'
import { gameStore } from '@stores'
import { useI18n } from 'vue-i18n'
import {
  oAuthLoginRequest,
  getSSOInfoRequest,
  oAuthUrlRequest,
} from '@services'

const ssoInfoState = ref({})

const WINDOW_NAME = 'embed-sso'
const WINDOW_WIDTH = 580
const WINDOW_HEIGHT = 820

export default function () {
  const { t } = useI18n()
  const { getPublicGame } = gameStore()
  const game = getPublicGame()
  const gameHidden = computed(() => game.value.status === STATUS_NOT_ACCEPTED)

  const loadingSSOLogin = ref(false)

  const {
    updateStatus: updateSSOLoginStatus,
    status: ssoLoginStatus,
    statusType,
  } = useStatus({
    loading: loadingSSOLogin,
  })

  let popup = null
  let loginType = null
  let interval = null

  const { run, error, data } = useAsync((type) =>
    oAuthUrlRequest(type, type === PORTAL_STUDIO ? GAME_ID : null)
  )

  const {
    loading: loadingSSOInfo,
    error: errorSSOInfo,
    data: dataSSOInfo,
    run: runSSOInfo,
  } = useAsync(getSSOInfoRequest)

  function onPopupClosed() {
    popup = null
    loginType = null
    window.removeEventListener('message', onPopupMessage)
    clearInterval(interval)
    interval = null
    loadingSSOLogin.value = false
  }

  async function onPopupMessage(e) {
    if (e.origin === location.origin && e.source.name === WINDOW_NAME) {
      try {
        const query = new URLSearchParams(e.data)
        if (
          (query.has('code') || query.has('openid.ns')) &&
          query.has('state')
        ) {
          await oAuthLoginRequest(loginType, e.data)
        }
      } catch (error) {
        console.error(error)
        addToast({
          title: t('login_fail'),
          isError: true,
          text: t('login_fail_message'),
        })
        setGlobalModal({
          modal: MODAL_LOGIN,
          data: { noAccess: gameHidden.value },
        })
      }

      popup?.close()
      onPopupClosed()

      if (!gameHidden.value) {
        clearGlobalModal()
      }
    }
  }

  function pollWindow() {
    if (!popup || popup.closed) {
      setGlobalModal({
        modal: MODAL_LOGIN,
        data: { noAccess: gameHidden.value },
      })
      onPopupClosed()
    }
  }

  async function loginSSO(type) {
    loadingSSOLogin.value = true
    await run(type)

    if (!error.value && data.value) {
      loginType = type
      const y = window.outerHeight / 2 + window.screenY - WINDOW_HEIGHT / 2
      const x = window.outerWidth / 2 + window.screenX - WINDOW_WIDTH / 2
      popup = window.open(
        data.value,
        WINDOW_NAME,
        `popup=true,width=${WINDOW_WIDTH},height=${WINDOW_HEIGHT},left=${x},top=${y}`
      )
      window.addEventListener('message', onPopupMessage)
      interval = setInterval(pollWindow, 500)
    } else {
      loadingSSOLogin.value = false
      updateSSOLoginStatus(statusType.ERROR)
      addToast({
        title: t('login_fail'),
        isError: true,
        text: t('login_fail_message'),
      })
      setTimeout(() => {
        setGlobalModal({
          modal: MODAL_LOGIN,
          data: { noAccess: gameHidden.value },
        })
      }, BUTTON_ANIMATION_DURATION)
    }
  }

  async function getSSOInfo(game) {
    if (game?.sso_mapping && !ssoInfoState.value[game.id]) {
      await runSSOInfo(game.id)

      if (!errorSSOInfo.value) {
        setSSOInfo(game.id, dataSSOInfo.value)
      }
    }
  }

  function setSSOInfo(gameId, info) {
    ssoInfoState.value[gameId] = {
      ...info,
    }
  }

  return {
    ssoInfo: readonly(ssoInfoState),
    loading: loadingSSOInfo,
    loadingSSOLogin,
    ssoLoginStatus,
    setSSOInfo,
    getSSOInfo,
    loginSSO,
  }
}
